import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik'
import * as Yup from 'yup'
import { PageProperties } from '../types'
import { pb, getCustomerLease, prepareLeasGeneration } from '../api-utils'
import { Customer, Lease } from '../site/pbTypes'
import { separator } from '../App'
import { useLeasePdfFlow } from './LeasePage/hooks/useLeasePdfFlow'
import Spinner from '../components/Spinner'
import ErrorModal from '../components/ErrorModal'

// ─── Build authenticated PocketBase file URL ─────────────────────────────────

function getLeaseFileUrl(lease: Lease & { signed_lease_url?: string }): string | null {
    if (!lease.signed_lease) return null
    const base = pb.getFileUrl(lease, lease.signed_lease)
    const token = pb.authStore.token
    return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

// ─── URL param helpers ───────────────────────────────────────────────────────

function useLeasePageParams() {
    const params = new URLSearchParams(window.location.search)
    return {
        hasSigned: params.get('hasSigned') === 'true',
        // Our own correlation id, embedded server-side into the redirect URL.
        leaseId: params.get('lease_id'),
    }
}

// ─── Validation schema for the lease generation form ─────────────────────────

const vehicleSchema = Yup.object({
    type: Yup.string().required('Vehicle type is required'),
    year: Yup.string().required('Year is required'),
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    lic_plate_state: Yup.string().required('License plate state is required'),
    lic_plate_number: Yup.string().required('License plate number is required'),
    vin: Yup.string().required('VIN is required'),
    space_identifier: Yup.string(),
    description: Yup.string(),
    proof_of_insurance: Yup.mixed().required('Proof of insurance is required'),
})

const leaseFormSchema = Yup.object({
    move_in_date: Yup.string().required('Move-in date is required'),
    vehicles: Yup.array().of(vehicleSchema).min(1, 'At least one vehicle is required'),
    driver_lic_front: Yup.mixed(),
    driver_lic_back: Yup.mixed(),
})

const emptyVehicle = {
    type: '',
    year: '',
    make: '',
    model: '',
    lic_plate_state: '',
    lic_plate_number: '',
    vin: '',
    space_identifier: '',
    description: '',
    proof_of_insurance: null as File | null,
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: '1rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.25rem',
    color: '#232043',
}

const errorStyle: React.CSSProperties = {
    color: 'red',
    fontSize: '0.8rem',
    marginTop: '0.2rem',
}

const sectionStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 8,
    padding: '1.25rem',
    marginBottom: '1.5rem',
}

const primaryBtnStyle: React.CSSProperties = {
    background: '#232043',
    color: 'yellow',
    border: 'none',
    borderRadius: 4,
    padding: '0.6em 1.8em',
    cursor: 'pointer',
    fontWeight: 700,
    letterSpacing: 1.1,
    fontSize: '1rem',
    fontFamily: 'inherit',
}

const secondaryBtnStyle: React.CSSProperties = {
    background: 'transparent',
    color: '#232043',
    border: '1px solid #232043',
    borderRadius: 4,
    padding: '0.4em 1.2em',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    fontFamily: 'inherit',
}

// ─── Vehicle type options ─────────────────────────────────────────────────────

const VEHICLE_TYPES = ['RV', 'Boat', 'Trailer', 'Camper', 'Motorhome', 'Other']

// ─── PDF Viewer component ─────────────────────────────────────────────────────

const LeaseViewer = ({ url, fileName }: { url: string; fileName: string }) => {
    const handlePrint = () => {
        const win = window.open(url, '_blank')
        win?.addEventListener('load', () => win.print())
    }

    return (
        <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ ...primaryBtnStyle, textDecoration: 'none', display: 'inline-block' }}
                >
                    View Full PDF
                </a>
                <a
                    href={url}
                    download={fileName}
                    style={{ ...secondaryBtnStyle, textDecoration: 'none', display: 'inline-block' }}
                >
                    Download
                </a>
                <button type="button" style={secondaryBtnStyle} onClick={handlePrint}>
                    Print
                </button>
            </div>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                <img
                    src={url}
                    alt="Signed Lease Agreement"
                    style={{
                        width: '100%',
                        borderRadius: 14,
                        border: '1px solid #ccc',
                    }}
                />
            </a>
        </div>
    )
}

// ─── Vehicle form section ─────────────────────────────────────────────────────

const VehicleSection = ({
    index,
    values,
    errors,
    touched,
    setFieldValue,
    remove,
    canRemove,
}: any) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const prefix = `vehicles[${index}]`
    const v = values.vehicles[index]
    const e: any = errors?.vehicles?.[index] || {}
    const t: any = touched?.vehicles?.[index] || {}

    return (
        <div style={{ ...sectionStyle, border: '1px solid #ddd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ color: '#232043', margin: 0 }}>Vehicle {index + 1}</h4>
                {canRemove && (
                    <button type="button" style={secondaryBtnStyle} onClick={() => remove(index)}>
                        Remove
                    </button>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                {/* Vehicle Type */}
                <div>
                    <label style={labelStyle} htmlFor={`${prefix}.type`}>Vehicle Type *</label>
                    <Field as="select" name={`${prefix}.type`} style={fieldStyle} id={`${prefix}.type`}>
                        <option value="">Select type...</option>
                        {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </Field>
                    {t.type && e.type && <div style={errorStyle}>{e.type}</div>}
                </div>

                {/* Year */}
                <div>
                    <label style={labelStyle} htmlFor={`${prefix}.year`}>Year *</label>
                    <Field name={`${prefix}.year`} id={`${prefix}.year`} placeholder="2019" style={fieldStyle} />
                    {t.year && e.year && <div style={errorStyle}>{e.year}</div>}
                </div>

                {/* Make */}
                <div>
                    <label style={labelStyle} htmlFor={`${prefix}.make`}>Make *</label>
                    <Field name={`${prefix}.make`} id={`${prefix}.make`} placeholder="Forest River" style={fieldStyle} />
                    {t.make && e.make && <div style={errorStyle}>{e.make}</div>}
                </div>

                {/* Model */}
                <div>
                    <label style={labelStyle} htmlFor={`${prefix}.model`}>Model *</label>
                    <Field name={`${prefix}.model`} id={`${prefix}.model`} placeholder="Wildwood" style={fieldStyle} />
                    {t.model && e.model && <div style={errorStyle}>{e.model}</div>}
                </div>

                {/* License Plate State */}
                <div>
                    <label style={labelStyle} htmlFor={`${prefix}.lic_plate_state`}>Plate State *</label>
                    <Field name={`${prefix}.lic_plate_state`} id={`${prefix}.lic_plate_state`} placeholder="OH" maxLength={2} style={fieldStyle} />
                    {t.lic_plate_state && e.lic_plate_state && <div style={errorStyle}>{e.lic_plate_state}</div>}
                </div>

                {/* License Plate Number */}
                <div>
                    <label style={labelStyle} htmlFor={`${prefix}.lic_plate_number`}>Plate Number *</label>
                    <Field name={`${prefix}.lic_plate_number`} id={`${prefix}.lic_plate_number`} placeholder="ABC1234" style={fieldStyle} />
                    {t.lic_plate_number && e.lic_plate_number && <div style={errorStyle}>{e.lic_plate_number}</div>}
                </div>

                {/* VIN */}
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle} htmlFor={`${prefix}.vin`}>VIN *</label>
                    <Field name={`${prefix}.vin`} id={`${prefix}.vin`} placeholder="1HGBH41JXMN109186" style={fieldStyle} />
                    {t.vin && e.vin && <div style={errorStyle}>{e.vin}</div>}
                </div>

                {/* Space Identifier */}
                <div>
                    <label style={labelStyle} htmlFor={`${prefix}.space_identifier`}>Space Number</label>
                    <Field name={`${prefix}.space_identifier`} id={`${prefix}.space_identifier`} placeholder="e.g. A7" style={fieldStyle} />
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>Your assigned storage space</div>
                </div>

                {/* Description */}
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle} htmlFor={`${prefix}.description`}>Description (optional)</label>
                    <Field
                        as="textarea"
                        name={`${prefix}.description`}
                        id={`${prefix}.description`}
                        placeholder="Color, special features, etc."
                        rows={2}
                        style={{ ...fieldStyle, resize: 'vertical' }}
                    />
                </div>
            </div>

            {/* Proof of Insurance */}
            <div style={{ marginTop: '0.75rem' }}>
                <label style={labelStyle}>Proof of Insurance *</label>
                {v.proof_of_insurance ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '0.9rem', color: '#232043' }}>
                            {(v.proof_of_insurance as File).name}
                        </span>
                        <button
                            type="button"
                            style={secondaryBtnStyle}
                            onClick={() => {
                                setFieldValue(`${prefix}.proof_of_insurance`, null)
                                if (fileInputRef.current) fileInputRef.current.value = ''
                            }}
                        >
                            Remove
                        </button>
                    </div>
                ) : (
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                            if (e.target.files?.[0]) {
                                setFieldValue(`${prefix}.proof_of_insurance`, e.target.files[0])
                            }
                        }}
                    />
                )}
                {t.proof_of_insurance && e.proof_of_insurance && (
                    <div style={errorStyle}>{e.proof_of_insurance as string}</div>
                )}
            </div>
        </div>
    )
}

// ─── Signing iframe view ──────────────────────────────────────────────────────

const SigningView = ({ signPageUrl }: { signPageUrl: string }) => (
    <div>
        <p style={{ color: '#232043', marginBottom: '0.5rem' }}>
            Please sign your storage agreement below. Check your email for a verification code if prompted.
        </p>
        <iframe
            src={`${signPageUrl}?embedded=yes`}
            id="leaseSignIframe"
            style={{ margin: '0.5rem 0', minWidth: '85%', minHeight: '700px', border: 'none' }}
            title="Sign your lease"
        />
    </div>
)

// ─── Main LeasePage component ─────────────────────────────────────────────────

const LeasePage = ({ page }: PageProperties) => {
    const navigate = useNavigate()
    const { hasSigned, leaseId } = useLeasePageParams()

    const [customer, setCustomer] = useState<Customer | null>(null)
    const [lease, setLease] = useState<(Lease & { signed_lease_url?: string }) | null>(null)
    const [loading, setLoading] = useState(true)
    const [signPageUrl, setSignPageUrl] = useState<string | undefined>()
    const [errorMessage, setErrorMessage] = useState<string | undefined>()
    const [showErrorModal, setShowErrorModal] = useState(false)

    const customerId = pb.authStore.record?.id || null

    // Post-sign PDF flow (no payment)
    const { loading: pdfFlowLoading, error: pdfFlowError } = useLeasePdfFlow(
        hasSigned,
        leaseId,
        customerId,
        (updatedLease) => {
            setLease(updatedLease)
            setLoading(false)
        }
    )

    // Show PDF flow errors in modal
    useEffect(() => {
        if (pdfFlowError) {
            setErrorMessage(pdfFlowError)
            setShowErrorModal(true)
        }
    }, [pdfFlowError])

    // On mount: fetch customer and existing lease
    useEffect(() => {
        if (hasSigned) return  // post-sign flow handled by useLeasePdfFlow

        const fetchData = async () => {
            if (!customerId) { setLoading(false); return }
            try {
                const [customerData, leaseResponse] = await Promise.all([
                    pb.collection('customers').getOne<Customer>(customerId, { $autoCancel: false }),
                    getCustomerLease(customerId),
                ])
                setCustomer(customerData)
                if (leaseResponse.success && leaseResponse.lease) {
                    setLease(leaseResponse.lease)
                }
            } catch (err: any) {
                console.error('Failed to load data:', err)
                setErrorMessage(err.message || 'Failed to load your account data')
                setShowErrorModal(true)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId])

    const gradientBg = `linear-gradient(${page.gradient?.direction}, ${page.gradient?.first}, ${page.gradient?.second})`

    const handleFormSubmit = async (values: any, { setSubmitting }: any) => {
        try {
            const redirectUrl = `${window.location.origin}/lease?hasSigned=true`

            const result = await prepareLeasGeneration({
                customerId: customerId!,
                vehicles: values.vehicles,
                driver_lic_front: values.driver_lic_front instanceof File ? values.driver_lic_front : undefined,
                driver_lic_back: values.driver_lic_back instanceof File ? values.driver_lic_back : undefined,
                move_in_date: values.move_in_date,
                redirect_url: redirectUrl,
            })

            if (!result.success) {
                throw new Error(result.error || 'Failed to prepare lease')
            }

            setSignPageUrl(result.sign_page_url)
            window.scrollTo(0, 0)
        } catch (err: any) {
            console.error('Lease generation error:', err)
            const msg = err.response?.data?.error || err.message || 'Failed to start lease signing'
            setErrorMessage(msg)
            setShowErrorModal(true)
        } finally {
            setSubmitting(false)
        }
    }

    // ── Render helpers ────────────────────────────────────────────────────────

    if (loading || pdfFlowLoading) {
        return (
            <div className="view force-full-view" style={{ padding: '8%', background: gradientBg }}>
                <div className="forceBlack subtitle">{pdfFlowLoading ? 'Processing your signed lease...' : 'Loading...'}</div>
                <Spinner />
            </div>
        )
    }

    return (
        <div className="view force-full-view" style={{ padding: '8%', background: gradientBg }}>
            <ErrorModal
                isOpen={showErrorModal}
                message={errorMessage}
                onClose={() => { setShowErrorModal(false); setErrorMessage(undefined) }}
            />

            {!lease?.signed_lease && (
                <h2 className="title is-4" style={{ color: '#232043', marginBottom: '0.5rem' }}>
                    {page.title}
                </h2>
            )}

            {/* ── Case 1: signing iframe is active ─────────────────────────── */}
            {signPageUrl && !hasSigned && (
                <>
                    <p style={{ color: '#232043', marginBottom: '1rem' }}>{page.subtitle}</p>
                    {separator}
                    <SigningView signPageUrl={signPageUrl} />
                </>
            )}

            {/* ── Case 2: signed lease PDF exists ──────────────────────────── */}
            {!signPageUrl && lease?.signed_lease && (
                <>
                    <LeaseViewer
                        url={getLeaseFileUrl(lease)!}
                        fileName={`lease-${lease.id}.pdf`}
                    />
                    {separator}
                    <button type="button" style={{ ...primaryBtnStyle, marginTop: '1rem' }} onClick={() => navigate('/customer')}>
                        Back to My Account
                    </button>
                </>
            )}

            {/* ── Case 3: no lease yet — show the generation form ──────────── */}
            {!signPageUrl && !lease?.signed_lease && (
                <>
                    <p style={{ color: '#232043', marginBottom: '1rem' }}>{page.subtitle}</p>
                    {separator}

                    <Formik
                        initialValues={{
                            move_in_date: '',
                            vehicles: [{ ...emptyVehicle }],
                            driver_lic_front: null as File | null,
                            driver_lic_back: null as File | null,
                        }}
                        validationSchema={leaseFormSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                            <Form>
                                {/* Move-in Date */}
                                <div style={{ ...sectionStyle, marginBottom: '1.5rem' }}>
                                    <h4 style={{ color: '#232043', marginBottom: '0.75rem' }}>Lease Details</h4>
                                    <div style={{ maxWidth: 280 }}>
                                        <label style={labelStyle} htmlFor="move_in_date">Move-in Date *</label>
                                        <Field type="date" name="move_in_date" id="move_in_date" style={fieldStyle} />
                                        <ErrorMessage name="move_in_date" render={msg => <div style={errorStyle}>{msg}</div>} />
                                    </div>
                                </div>

                                {/* Driver's License */}
                                <div style={sectionStyle}>
                                    <h4 style={{ color: '#232043', marginBottom: '0.75rem' }}>Driver's License</h4>
                                    <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.75rem' }}>
                                        Upload the front and back of your driver's license.
                                        {customer?.driver_lic_front && (
                                            <span style={{ color: 'green' }}> (Front already on file)</span>
                                        )}
                                        {customer?.driver_lic_back && (
                                            <span style={{ color: 'green' }}> (Back already on file)</span>
                                        )}
                                    </p>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div>
                                            <label style={labelStyle}>Front</label>
                                            {values.driver_lic_front ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span style={{ fontSize: '0.85rem' }}>{(values.driver_lic_front as File).name}</span>
                                                    <button type="button" style={secondaryBtnStyle} onClick={() => setFieldValue('driver_lic_front', null)}>Remove</button>
                                                </div>
                                            ) : (
                                                <input type="file" accept="image/*,.pdf" onChange={e => { if (e.target.files?.[0]) setFieldValue('driver_lic_front', e.target.files[0]) }} />
                                            )}
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Back</label>
                                            {values.driver_lic_back ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span style={{ fontSize: '0.85rem' }}>{(values.driver_lic_back as File).name}</span>
                                                    <button type="button" style={secondaryBtnStyle} onClick={() => setFieldValue('driver_lic_back', null)}>Remove</button>
                                                </div>
                                            ) : (
                                                <input type="file" accept="image/*,.pdf" onChange={e => { if (e.target.files?.[0]) setFieldValue('driver_lic_back', e.target.files[0]) }} />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Vehicles */}
                                <FieldArray name="vehicles">
                                    {({ remove, push }) => (
                                        <>
                                            {values.vehicles.map((_, index) => (
                                                <VehicleSection
                                                    key={index}
                                                    index={index}
                                                    values={values}
                                                    errors={errors}
                                                    touched={touched}
                                                    setFieldValue={setFieldValue}
                                                    remove={remove}
                                                    canRemove={values.vehicles.length > 1}
                                                />
                                            ))}
                                            <button
                                                type="button"
                                                style={{ ...secondaryBtnStyle, marginBottom: '1.5rem' }}
                                                onClick={() => push({ ...emptyVehicle })}
                                            >
                                                + Add Another Vehicle
                                            </button>
                                        </>
                                    )}
                                </FieldArray>

                                {separator}

                                <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <button type="submit" style={primaryBtnStyle} disabled={isSubmitting}>
                                        {isSubmitting ? 'Preparing...' : 'Review & Sign My Lease'}
                                    </button>
                                    <button type="button" style={secondaryBtnStyle} onClick={() => navigate('/customer')}>
                                        Cancel
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </>
            )}
        </div>
    )
}

export default LeasePage
